import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import StarIcon from '@mui/icons-material/Star';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';

import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';

import Query, {
  ServiceGroupsListRefetchQuery,
} from '__generated__/ServiceGroupsListRefetchQuery.graphql';
import { ServiceGroupsList_data$key } from '__generated__/ServiceGroupsList_data.graphql';
import ServiceGroupsListRow from 'components/ServiceGroupsListRow';
import ServiceGroupsListAdd from 'components/ServiceGroupsListAdd';

type Props = {
  data: ServiceGroupsList_data$key;
};

const ServiceGroupsList: FC<Props> = ({ data }) => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ serviceGroups }, { refetch, loading }] = useAvoidSuspense<
    ServiceGroupsListRefetchQuery,
    ServiceGroupsList_data$key
  >(
    graphql`
      fragment ServiceGroupsList_data on Query
      @refetchable(queryName: "ServiceGroupsListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        serviceGroups(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...ServiceGroupsListRow_serviceGroup
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionId = serviceGroups?.__id;

  const [deleteCountry, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = serviceGroups ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<StarIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <ServiceGroupsListRow
            key={node.id}
            serviceGroup={node}
            onDelete={deleteCountry}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <ServiceGroupsListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default ServiceGroupsList;
